import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatNumber(num) {
  return (Math.round((num || 0) * 100) / 100).toLocaleString('cs-CZ');
}

/**
 * TeamleaderTab — "Můj team"
 * Teamleader (uživatel s teamleaderFor) čerpá odměnu z GP podřízených dle nastavených pásem.
 * TL vidí kompletní přehled podpisů + GP podřízených.
 * Admin: filtr zobrazí jen uživatele s podřízenými, spravuje pásma ("Teamleader - nastavení")
 * a zaškrtává, kteří podřízení se započítávají.
 */
export default function TeamleaderTab({ userRole, users, currentUserId, isDarkMode }) {
  const isAdmin = userRole === 'admin';
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [teamleaders, setTeamleaders] = useState([]);
  const [selectedUser, setSelectedUser] = useState(isAdmin ? '' : currentUserId);
  const [data, setData] = useState({ isTeamleader: false, totalReward: 0, subordinates: [], tiers: [] });
  const [loading, setLoading] = useState(false);
  const [savingCounted, setSavingCounted] = useState(false);

  // Nastavení pásem (admin)
  const [showSettings, setShowSettings] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [savingTiers, setSavingTiers] = useState(false);
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [availableMonths, setAvailableMonths] = useState([]);

  // Načti dostupné měsíce a nastav výchozí na poslední měsíc s daty
  // (aktuální měsíc často ještě nemá nahrané provize → jinak by se nic nezobrazilo).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/commissions/months`);
        if (!res.ok) return;
        const json = await res.json();
        const months = Array.isArray(json.months) ? [...json.months].sort() : [];
        if (cancelled || months.length === 0) return;
        setAvailableMonths(months);
        // Pokud aktuálně vybraný měsíc nemá data, přepni na nejnovější dostupný.
        if (!months.includes(getCurrentMonth())) {
          setSelectedMonth(months[months.length - 1]);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Načti seznam teamleaderů (admin filtr)
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/teamleaders?userId=${currentUserId || ''}`);
        if (res.ok) setTeamleaders(await res.json());
      } catch { /* ignore */ }
    })();
  }, [isAdmin, currentUserId]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const effectiveUser = isAdmin ? selectedUser : currentUserId;
      let url = `${API_URL}/teamleader-reward?month=${selectedMonth}&userId=${currentUserId || ''}`;
      if (effectiveUser) url += `&filterUserId=${effectiveUser}`;
      const res = await fetch(url);
      const json = res.ok ? await res.json() : { isTeamleader: false, totalReward: 0, subordinates: [], tiers: [] };
      setData(json);
    } catch {
      setData({ isTeamleader: false, totalReward: 0, subordinates: [], tiers: [] });
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedUser, currentUserId, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadTiers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/teamleader-config?userId=${currentUserId || ''}`);
      if (res.ok) {
        const json = await res.json();
        setTiers(json.tiers || []);
        setFeatureEnabled(json.enabled !== false);
      }
    } catch { /* ignore */ }
  }, [currentUserId]);

  const openSettings = async () => {
    await loadTiers();
    setShowSettings(true);
  };

  const handleSaveTiers = async () => {
    setSavingTiers(true);
    try {
      const res = await fetch(`${API_URL}/teamleader-config?userId=${currentUserId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiers, enabled: featureEnabled, actorUserId: currentUserId })
      });
      if (res.ok) {
        setShowSettings(false);
        await fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Chyba při ukládání nastavení');
      }
    } catch {
      alert('Chyba připojení');
    } finally {
      setSavingTiers(false);
    }
  };

  // Admin: přepnutí započítávání podřízeného
  const handleToggleCounted = async (subUserId, currentlyCounted) => {
    if (!isAdmin || !selectedUser) return;
    setSavingCounted(true);
    // Nová sada vyloučených
    const currentlyExcluded = data.subordinates.filter(s => !s.counted).map(s => s.userId);
    let newExcluded;
    if (currentlyCounted) {
      newExcluded = [...new Set([...currentlyExcluded, subUserId])];
    } else {
      newExcluded = currentlyExcluded.filter(id => id !== subUserId);
    }
    try {
      const res = await fetch(`${API_URL}/users/${selectedUser}/teamleader-counted?userId=${currentUserId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ excludedSubordinates: newExcluded, actorUserId: currentUserId })
      });
      if (res.ok) {
        await fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Chyba při změně započítávání');
      }
    } catch {
      alert('Chyba připojení');
    } finally {
      setSavingCounted(false);
    }
  };

  const updateTier = (idx, field, value) => {
    setTiers(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };
  const addTier = () => setTiers(prev => [...prev, { minGp: 0, maxGp: null, multiplier: 0 }]);
  const removeTier = (idx) => setTiers(prev => prev.filter((_, i) => i !== idx));

  const card = `rounded-lg border p-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} shadow-sm`;
  const inputCls = `w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const th = `px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`;
  const td = `px-3 py-2 text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`;
  const tdMuted = `px-3 py-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`;

  const effectiveUser = isAdmin ? selectedUser : currentUserId;

  return (
    <div>
      {/* Filtry */}
      <div className={`flex flex-wrap gap-4 items-end mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
        <div>
          <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Měsíc</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
          />
        </div>
        {isAdmin && (
          <div>
            <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Teamleader</label>
            <select
              value={selectedUser}
              onChange={e => setSelectedUser(e.target.value)}
              className={`border rounded px-2 py-1 text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
            >
              <option value="">— Vyberte teamleadera —</option>
              {teamleaders.map(tl => (
                <option key={tl._id} value={tl._id}>{tl.name || tl.email} ({tl.subordinateCount})</option>
              ))}
            </select>
          </div>
        )}
        {isAdmin && (
          <button
            onClick={openSettings}
            className="ml-auto text-sm px-3 py-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white"
          >⚙️ Teamleader - nastavení</button>
        )}
      </div>

      {loading ? (
        <div className={`text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Načítám…</div>
      ) : (isAdmin && !effectiveUser) ? (
        <div className={`text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Vyberte teamleadera pro zobrazení odměn.</div>
      ) : !data.isTeamleader ? (
        <div className={`text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {isAdmin ? 'Tento uživatel nemá žádné podřízené.' : 'Nejste teamleaderem žádného obchodníka.'}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Souhrn odměny */}
          <div className={card}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className={`font-semibold text-lg mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>👥 Můj team – odměna</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Odměna z GP započítaných podřízených za měsíc.
                </p>
                {data.enabled === false && (
                  <p className="text-sm mt-1 font-semibold text-amber-500">
                    ⚠️ Odměny teamleaderů jsou vypnuté – nezapočítávají se do shrnutí.
                  </p>
                )}
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-green-500">{formatNumber(data.totalReward)} Kč</div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>odměna teamleadera</div>
              </div>
            </div>
          </div>

          {/* Přehled podřízených */}
          <div className={card}>
            <h3 className={`font-semibold text-sm mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Podřízení obchodníci ({data.subordinates.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    {isAdmin && <th className={th}>Započítat</th>}
                    <th className={th}>Obchodník</th>
                    <th className={th}>Počet podpisů</th>
                    <th className={th}>GP</th>
                    <th className={th}>Násobitel</th>
                    <th className={th}>Odměna</th>
                  </tr>
                </thead>
                <tbody>
                  {data.subordinates.map(s => (
                    <tr key={s.userId} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} ${!s.counted ? 'opacity-50' : ''}`}>
                      {isAdmin && (
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={s.counted}
                            disabled={savingCounted}
                            onChange={() => handleToggleCounted(s.userId, s.counted)}
                            className="w-4 h-4 rounded cursor-pointer"
                          />
                        </td>
                      )}
                      <td className={`${td} font-medium`}>{s.userName}</td>
                      <td className={td}>{s.signings.length}</td>
                      <td className={`${td} font-semibold`}>{formatNumber(s.gp)} Kč</td>
                      <td className={tdMuted}>{s.multiplier}×</td>
                      <td className={`${td} font-bold ${s.counted ? 'text-green-500' : ''}`}>{formatNumber(s.reward)} Kč</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={`border-t-2 ${isDarkMode ? 'border-gray-500' : 'border-gray-300'}`}>
                    <td className={`${td} font-bold`} colSpan={isAdmin ? 5 : 4}>Celkem (započítaní)</td>
                    <td className={`${td} font-bold text-green-500`}>{formatNumber(data.totalReward)} Kč</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Detail podpisů podřízených */}
          {data.subordinates.filter(s => s.signings.length > 0).map(s => (
            <div key={`sign-${s.userId}`} className={card}>
              <h4 className={`font-semibold text-sm mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                📝 {s.userName} – podpisy ({s.signings.length}), GP {formatNumber(s.gp)} Kč
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <th className={th}>Zákazník</th>
                      <th className={th}>GP</th>
                      <th className={th}>Provize</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.signings.map((sig, idx) => (
                      <tr key={idx} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                        <td className={`${td} font-medium`}>{sig.companyName || '—'}</td>
                        <td className={td}>{formatNumber(sig.gp)} Kč</td>
                        <td className={tdMuted}>{formatNumber(sig.commission)} Kč</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal nastavení pásem */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden m-4`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Teamleader - nastavení pásem</h2>
              <button onClick={() => setShowSettings(false)} className={isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}>✕</button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-140px)]">
              <div className={`flex items-center justify-between gap-4 mb-4 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <div>
                  <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Odměny teamleaderů</div>
                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Když je vypnuto, odměna teamleadera se nezapočítává do shrnutí.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setFeatureEnabled(v => !v)}
                  role="switch"
                  aria-checked={featureEnabled}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${featureEnabled ? 'bg-green-600' : (isDarkMode ? 'bg-gray-500' : 'bg-gray-300')}`}
                  title={featureEnabled ? 'Zapnuto' : 'Vypnuto'}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${featureEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Násobitel GP podřízeného obchodníka podle jeho GP za měsíc. Pásmo platí pro GP ≥ Od a &lt; Do (Do prázdné = neomezeno).
              </p>
              <table className="w-full border-collapse mb-4">
                <thead>
                  <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <th className={th}>GP od (Kč)</th>
                    <th className={th}>GP do (Kč)</th>
                    <th className={th}>Násobitel</th>
                    <th className={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((t, idx) => (
                    <tr key={idx} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                      <td className="px-2 py-2">
                        <input type="number" value={t.minGp ?? 0} onChange={e => updateTier(idx, 'minGp', e.target.value)} className={inputCls} />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" value={t.maxGp ?? ''} placeholder="neomezeno" onChange={e => updateTier(idx, 'maxGp', e.target.value === '' ? null : e.target.value)} className={inputCls} />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" step="0.1" value={t.multiplier ?? 0} onChange={e => updateTier(idx, 'multiplier', e.target.value)} className={inputCls} />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <button onClick={() => removeTier(idx)} className="text-red-500 hover:text-red-700 text-lg" title="Odebrat pásmo">🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={addTier} className={`text-sm px-3 py-1.5 rounded ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>+ Přidat pásmo</button>
            </div>
            <div className={`flex justify-end gap-2 p-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button onClick={() => setShowSettings(false)} className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>Zrušit</button>
              <button onClick={handleSaveTiers} disabled={savingTiers} className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
                {savingTiers ? 'Ukládám…' : 'Uložit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
